import { useState } from 'react'
import type { CatalogSummary } from '~/api/types'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Card } from '~/components/ui/card'
import { cn } from '~/lib/utils'

// 카탈로그 워크벤치 요약 스트립(접기 가능·가로). 총 항목/필드/업종 수 + 결정형,
// 규모·분류·단계·데이터유형 분포를 배지+수로 고밀도 표시. summary API 기반.

const SCALE_TONE: Record<string, BadgeTone> = {
    general: 'accent',
    small: 'info',
}
const DATA_TYPE_TONE: Record<string, BadgeTone> = {
    정량: 'info',
    정성: 'warning',
}

export function SummaryStrip({ summary }: { summary: CatalogSummary | null }) {
    const [open, setOpen] = useState(true)

    return (
        <Card className='mb-5'>
            <div className='flex items-center justify-between px-4 py-2.5'>
                <div className='flex flex-wrap items-center gap-x-5 gap-y-1'>
                    <Metric label='총 항목' value={summary?.totalItems} />
                    <Metric label='필드' value={summary?.totalFields} />
                    <Metric label='업종' value={summary?.totalIndustries} />
                    <Metric
                        label='결정형'
                        value={summary?.deterministicCount}
                        tone='info'
                    />
                </div>
                <button
                    type='button'
                    aria-expanded={open}
                    onClick={() => setOpen((o) => !o)}
                    className='text-primary text-xs font-bold'>
                    {open ? '분포 접기' : '분포 펼치기'}
                </button>
            </div>

            {open ? (
                <div className='border-border grid gap-x-6 gap-y-3 border-t px-4 py-3 sm:grid-cols-2 lg:grid-cols-4'>
                    <Distribution
                        title='규모'
                        counts={summary?.byScale}
                        tones={SCALE_TONE}
                        labels={{ general: '일반', small: '소규모' }}
                    />
                    <Distribution
                        title='분류'
                        counts={summary?.byClassification}
                    />
                    <Distribution
                        title='단계'
                        counts={summary?.byAssessmentType}
                    />
                    <Distribution
                        title='데이터 유형'
                        counts={summary?.byDataType}
                        tones={DATA_TYPE_TONE}
                    />
                </div>
            ) : null}
        </Card>
    )
}

function Metric({
    label,
    value,
    tone,
}: {
    label: string
    value?: number
    tone?: 'info'
}) {
    return (
        <div className='flex items-baseline gap-1.5'>
            <span className='krds-label'>{label}</span>
            <span
                className={cn(
                    'text-base font-bold tabular-nums',
                    tone === 'info' ? 'text-info' : 'text-foreground'
                )}>
                {value?.toLocaleString() ?? '-'}
            </span>
        </div>
    )
}

function Distribution({
    title,
    counts,
    tones,
    labels,
}: {
    title: string
    counts?: Record<string, number>
    tones?: Record<string, BadgeTone>
    labels?: Record<string, string>
}) {
    const entries = Object.entries(counts ?? {})
    return (
        <div>
            <div className='krds-label mb-1.5'>{title}</div>
            {entries.length === 0 ? (
                <span className='text-muted-foreground text-xs'>
                    {counts ? '데이터 없음' : '—'}
                </span>
            ) : (
                <div className='flex flex-wrap gap-1.5'>
                    {entries.map(([key, count]) => (
                        <Badge key={key} tone={tones?.[key] ?? 'neutral'}>
                            {labels?.[key] ?? key} · {count}
                        </Badge>
                    ))}
                </div>
            )}
        </div>
    )
}
